import { useLang } from "hooks/useLang";
import React, { useEffect, useMemo, useRef, useState } from "react";
import convertArNumbersToEn from "utils/helpers/convertArNumbersToEn";
import CallingCodeSelector from "./CallingCodeSelector";
import useDebounce from "hooks/useDebounce";
import { validation } from "utils/helpers/validation";

interface IProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  setCallingCode: (callingCode: string) => void;
  setPhone: (phone: string) => void;
  setIsValidPhone?: (isValid: boolean) => void;
  callingCode: string;
  phone: string;
  error?: string;
  label?: string;
}

const PhoneInput = ({
  callingCode,
  setCallingCode,
  setPhone,
  setIsValidPhone,
  phone,
  error,
  label: customLabel,
  ...props
}: IProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useLang();
  const [isValidPhone, setIsValidPhoneLocal] = useState(true);
  const callingCodeSelectorRef = useRef<boolean>(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const debouncedCallingCode = useDebounce(callingCode, 500);
  const debouncedPhone = useDebounce(phone, 500);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const style = useMemo(() => {
    return {
      label: `text-sm capitalize transition-all md:text-base pb-1 text-gray-600 dark:text-gray-200 ${props.required ? "after:content-['*'] after:text-red-500" : ""
        }`,
      inputContainer: `flex border rounded-md transition-all h-9 inputContainer bg-white dark:bg-opacity-20 bg-opacity-40 dark:bg-primary-theme-500 ${isFocused ? "border-blue-500" : "border-gray-300"
        } ${error || !isValidPhone ? "border-red-500" : ""}`,
      input: "h-full w-full bg-transparent p-2 text-sm",
    };
  }, [isFocused, error, isValidPhone]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim();
    const sanitizedPhone = convertArNumbersToEn(inputValue);

    if (validation.isDigitsOnly(sanitizedPhone)) {
      setPhone(sanitizedPhone);
      setHasInteracted(true);
    }
  };

  const validatePhoneNumber = () => {
    if (!hasInteracted) return;

    const isValid = validation.isPhoneValid(debouncedPhone);
    setIsValidPhoneLocal(isValid);
    setIsValidPhone?.(isValid);
  };

  useEffect(() => {
    if (callingCode[0] === "+") {
      setCallingCode(callingCode.substring(1));
    }
  }, [callingCode]);

  useEffect(() => {
    validatePhoneNumber();
  }, [debouncedCallingCode, debouncedPhone, hasInteracted]);

  useEffect(() => {
    return () => {
      if (callingCodeSelectorRef.current) {
        setCallingCode("966");
      }
    };
  }, []);

  return (
    <div className="flex w-full flex-col">
      <label className={style.label} htmlFor={"phone-input-id"}>
        {customLabel ? customLabel : t("phoneNumber")}{" "}
      </label>
      <div
        dir="ltr"
        className={style.inputContainer}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <CallingCodeSelector
          setCallingCode={(code) => {
            setCallingCode(code);
            setHasInteracted(false);
          }}
          callingCode={callingCode}
          disable={props.readOnly ? true : false}
        />
        <input
          id="phone-input-id"
          className={style.input}
          onChange={handlePhoneChange}
          value={phone}
          autoComplete="tel"
          maxLength={9}
          {...props}
        />
      </div>
      {hasInteracted && (!isValidPhone || error) && (
        <span className="pt-1 text-xs text-red-500 dark:text-red-400">
          {t(error || "") || t("invalidPhoneNumber")}
        </span>
      )}
    </div>
  );
};

export default PhoneInput;
